import React from "react";
import get from "lodash/get";
import sortBy from "lodash/sortBy";
import { connect } from "react-redux";
import {
  Alert,
  Icon,
  Layout,
  Input,
  Tooltip,
  Button,
  Modal,
  Table,
  Switch,
  message,
} from "antd";
import classNames from "classnames";

import AdminHeader from "../../components/AdminHeader/AdminHeader";
import Footer from "../../components/Footer/Footer";
import PatientProfileForm from "../../components/PatientProfileForm/PatientProfileForm";
import PatientConsentGivenForm from "../../components/PatientProfileForm/PatientConsentGivenForm";
import PatientAdjudicatedConsentStatusForm from "../../components/PatientProfileForm/PatientAdjudicatedConsentStatusForm";
import classes from "./PatientProfile.css";

import isAuthenticated from "../../utils/isAuthenticated";
import {
  getSubjectRequest,
  deletePatientProfileDocumentRequest,
  activatePatientProfileDocumentRequest,
} from "../../api/requests";
import { logout } from "../../reducers/user/actions";
import {
  getPatientProfileReadPermission,
  getPatientDocumentCreatePermission,
  getConsentGivenUpdatePermission,
} from "../../reducers/user/selectors";
import PatientRegistryForm from "../../components/PatientProfileForm/PatientRegistryForm";
import PatientEmailStatusForm from "../../components/PatientProfileForm/PatientEmailStatusForm";
import PatientCoenrollmentForm from "../../components/PatientProfileForm/PatientCoenrollmentForm";
import DocumentForm, {
  documentTypes,
} from "../../components/DocumentForm/DocumentForm";
import FileLink from "../../components/FileLink/FileLink";

const { Content } = Layout;
const { Search } = Input;

class PatientProfile extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      subject: null,
      subjectSearch: null,
      subjectID: null,
      errorMessage: null,
      responseMessage: null,
      isLoading: false,
      showRegistryForm: false,
      showEmailStatusForm: false,
      showConsentGivenForm: false,
      showAdjudicatedConsentStatusForm: false,
      showDocumentsSection: false,
      showCoenrollmentForm: false,
      documentEdit: null,
      order: null,
      sortedInfo: { columnKey: 'order_index', order: null },
    };
  }

  componentDidMount() {
    this.fetchCurrentSubject();
  }

  fetchCurrentSubject({ withLoading = true } = {}) {
    const subjectID = get(this.props, "match.params.subjectID");

    if (subjectID) {
      this.onSubjectSearch(subjectID, { withLoading });
      this.setState({ subjectSearch: subjectID });
    }
  }

  onSubjectSearch = async (value) => {
    if (value.trim()) {
      this.setState({
        subjectID: value,
        errorMessage: null,
        isLoading: true,
        responseMessage: null,
        subject: null,
      });

      try {
        const { data } = await getSubjectRequest(value);
        this.setState({
          subject: {
            ...data,
            profile: data.profile ? { ...data.profile } : null,
          },
        });
      } catch (e) {
        if (get(e, "response.status") === 401) {
          this.props.logout();
          return;
        }
        this.setState({
          subject: null,
          errorMessage: get(e, "response.data.error", "Error"),
        });
      }

      this.setState({ isLoading: false });
    }
  };

  onProfileDelete = () => {
    this.setState({
      subject: null,
      responseMessage: "Successfully deactivated",
    });
  };

  onSubjectChange = (e) => {
    this.setState({ subjectSearch: e.target.value });
  };

  collapseSection = (sectionName) => () => {
    this.setState((state) => ({ [sectionName]: !state[sectionName] }));
  };

  addDocument = () => {
    this.setState({ documentEdit: {} });
  };

  onDocumentEdit = (document) => () => {
    this.setState({ documentEdit: document });
  };

  onDocumentEditHide = () => {
    this.setState({ documentEdit: null });
  };

  onDocumentEditSubmit = () => {
    this.setState({ documentEdit: null }, () =>
      this.fetchCurrentSubject({ withLoading: false })
    );
  };

  onSwitchDocumentActivation = (id, state) => async () => {
    try {
      if (state === "active") {
        await deletePatientProfileDocumentRequest(id);
      } else {
        await activatePatientProfileDocumentRequest(id);
      }

      message.success("Saved");

      this.fetchCurrentSubject({ withLoading: false });
    } catch (e) {
      if (get(e, "response.status") === 401) {
        this.props.logout();
        return;
      }
      message.error("Error");
      console.log(e);
    }
  };

  documentColumns() {
    return [
      {
        dataIndex: "order_index",
        key: "order_index",
        title: "Index",
        render: val => (<span>{val}</span>),
        sorter: true,
        sortOrder: get(this.state.sortedInfo, 'columnKey') === 'order_index' && this.state.sortedInfo.order,
      },
      {
        dataIndex: "name",
        key: "name",
        title: "Document Name",
        render: (val, record) => <div>{val}</div>,
      },
      {
        dataIndex: "category",
        key: "category",
        title: "Document Category",
        render: (val, record) => <div>Patient documents</div>,
      },
      {
        dataIndex: "type",
        key: "type",
        title: "Document Type",
        render: (val, record) => <div>{documentTypes[val]}</div>,
      },
      {
        dataIndex: "start_year",
        key: "start_year",
        title: "Start date",
      },
      {
        dataIndex: "expiry_year",
        key: "expiry_year",
        title: "Expiry date",
      },
      {
        dataIndex: "id",
        key: "id",
        title: "Actions",
        render: (val, document) => (
          <div className={classes.actions}>
            <span style={{ position: 'relative', top: '8px' }}>
              <FileLink url={document.url} />
            </span>
            &nbsp; &nbsp;
            <Tooltip placement="topLeft" title="Edit Document">
              <Icon
                type="edit"
                theme="filled"
                onClick={this.onDocumentEdit(document)}
              />
            </Tooltip>
            &nbsp; &nbsp;
            <Tooltip placement="topLeft" title="Activate / Deactivate Document">
              <Switch
                checked={document.state === "active"}
                onChange={this.onSwitchDocumentActivation(val, document.state)}
              />
            </Tooltip>
          </div>
        ),
      },
    ];
  }

  handleTableChange = (pagination, filters, sorter) => {
    this.setState({
      sortedInfo: sorter,
      order: get(sorter, 'order') === 'ascend' ? 'asc' : get(sorter, 'order') === 'descend' ? 'desc' : 'asc',
    });
  };

  render() {
    const {
      subject,
      subjectID,
      errorMessage,
      isLoading,
      responseMessage,
      subjectSearch,
      showRegistryForm,
      showEmailStatusForm,
      showConsentGivenForm,
      showAdjudicatedConsentStatusForm,
      showDocumentsSection,
      showCoenrollmentForm,
      documentEdit,
      order,
    } = this.state;
    const {
      patientProfileReadPermission,
      patientDocumentCreatePermission,
      consentGivenUpdatePermission,
    } = this.props;

    return (
      <div style={{ backgroundColor: "#FAFAFA" }}>
        <Layout className={classes.parentContainer}>
          <AdminHeader />
          <Content className={classes.content}>
            <h3 className="headline-default">Patient profile</h3>

            {!patientProfileReadPermission && (
              <Alert
                type="error"
                message="Permission denied! You cannot perform this action."
              />
            )}

            {patientProfileReadPermission && (
              <div>
                <div className={classes.searchWrapper}>
                  <label>
                    <b className={classes.searchWrapperLabel}>SEARCH</b>
                    <Search
                      placeholder="By subject ID"
                      enterButton="Search"
                      size="large"
                      onSearch={this.onSubjectSearch}
                      value={subjectSearch}
                      onChange={this.onSubjectChange}
                      style={{ maxWidth: "500px" }}
                    />
                  </label>
                </div>

                {errorMessage && <Alert type="error" message={errorMessage} />}

                {responseMessage && (
                  <Alert type="success" message={responseMessage} />
                )}

                {subject && (
                  <div className={classes.registryFormWrapper}>
                    <h4
                      className={classNames(
                        "headline-default",
                        classes.toggleHeadline
                      )}
                      onClick={this.collapseSection("showEmailStatusForm")}
                    >
                      <Tooltip
                        title={showEmailStatusForm ? "collapse" : "show"}
                      >
                        <Icon
                          type="arrow-up"
                          className={classes.collapseIcon}
                          style={{
                            fontSize: "20px",
                            transform: `rotate(${
                              showEmailStatusForm ? "0" : "180deg"
                            })`,
                          }}
                        />{" "}
                        &nbsp;
                      </Tooltip>
                      <span className={classes.toggleText}>
                        Consent - Email status
                      </span>
                    </h4>

                    {showEmailStatusForm && (
                      <div>
                        <PatientEmailStatusForm
                          {...subject}
                          subjectID={subjectID}
                          onSubmit={this.onSubjectSearch}
                        />
                      </div>
                    )}
                  </div>
                )}

                {subject && consentGivenUpdatePermission && (
                  <div className={classes.registryFormWrapper}>
                    <h4
                      className={classNames(
                        "headline-default",
                        classes.toggleHeadline
                      )}
                      onClick={this.collapseSection("showConsentGivenForm")}
                    >
                      <Tooltip
                        title={showConsentGivenForm ? "collapse" : "show"}
                      >
                        <Icon
                          type="arrow-up"
                          className={classes.collapseIcon}
                          style={{
                            fontSize: "20px",
                            transform: `rotate(${
                              showConsentGivenForm ? "0" : "180deg"
                            })`,
                          }}
                        />{" "}
                        &nbsp;
                      </Tooltip>
                      <span className={classes.toggleText}>
                        Custom consent given
                      </span>
                    </h4>

                    {showConsentGivenForm && (
                      <div>
                        <PatientConsentGivenForm
                          {...subject}
                          subjectID={subjectID}
                          onSubmit={this.onSubjectSearch}
                        />
                      </div>
                    )}
                  </div>
                )}

                {subject && consentGivenUpdatePermission && (
                  <div className={classes.registryFormWrapper}>
                    <h4
                      className={classNames(
                        "headline-default",
                        classes.toggleHeadline
                      )}
                      onClick={this.collapseSection("showAdjudicatedConsentStatusForm")}
                    >
                      <Tooltip
                        title={showAdjudicatedConsentStatusForm ? "collapse" : "show"}
                      >
                        <Icon
                          type="arrow-up"
                          className={classes.collapseIcon}
                          style={{
                            fontSize: "20px",
                            transform: `rotate(${
                              showAdjudicatedConsentStatusForm ? "0" : "180deg"
                            })`,
                          }}
                        />{" "}
                        &nbsp;
                      </Tooltip>
                      <span className={classes.toggleText}>
                        Adjudicated consent status
                      </span>
                    </h4>

                    {showAdjudicatedConsentStatusForm && (
                      <div>
                        <PatientAdjudicatedConsentStatusForm
                          {...subject}
                          subjectID={subjectID}
                          onSubmit={this.onSubjectSearch}
                        />
                      </div>
                    )}
                  </div>
                )}

                {subject && (
                  <div className={classes.registryFormWrapper}>
                    <h4
                      className={classNames(
                        "headline-default",
                        classes.toggleHeadline
                      )}
                      onClick={this.collapseSection("showRegistryForm")}
                    >
                      <Tooltip title={showRegistryForm ? "collapse" : "show"}>
                        <Icon
                          type="arrow-up"
                          className={classes.collapseIcon}
                          style={{
                            fontSize: "20px",
                            transform: `rotate(${
                              showRegistryForm ? "0" : "180deg"
                            })`,
                          }}
                        />{" "}
                        &nbsp;
                      </Tooltip>
                      <span className={classes.toggleText}>Edit registry</span>
                    </h4>

                    {showRegistryForm && (
                      <div>
                        <PatientRegistryForm
                          {...subject}
                          subjectID={subjectID}
                          onSubmit={this.onSubjectSearch}
                        />
                      </div>
                    )}
                  </div>
                )}

                {subject && (
                  <div className={classes.registryFormWrapper}>
                    <h4
                      className={classNames(
                        "headline-default",
                        classes.toggleHeadline
                      )}
                      onClick={this.collapseSection("showCoenrollmentForm")}
                    >
                      <Tooltip title={showCoenrollmentForm ? "collapse" : "show"}>
                        <Icon
                          type="arrow-up"
                          className={classes.collapseIcon}
                          style={{
                            fontSize: "20px",
                            transform: `rotate(${
                              showCoenrollmentForm ? "0" : "180deg"
                            })`,
                          }}
                        />{" "}
                        &nbsp;
                      </Tooltip>
                      <span className={classes.toggleText}>Co-Enrollment</span>
                    </h4>

                    {showCoenrollmentForm && (
                      <div>
                        <PatientCoenrollmentForm
                          {...subject}
                          subjectID={subjectID}
                          onSubmit={this.onSubjectSearch}
                        />
                      </div>
                    )}
                  </div>
                )}

                {subject && (
                  <PatientProfileForm
                    {...subject}
                    subjectID={subjectID}
                    onSubmit={this.onSubjectSearch}
                    onProfileDelete={this.onProfileDelete}
                  />
                )}

                {subject && (
                  <div className={classes.registryFormWrapper}>
                    <h4
                      className={classNames(
                        "headline-default",
                        classes.toggleHeadline
                      )}
                      onClick={this.collapseSection("showDocumentsSection")}
                    >
                      <Tooltip
                        title={showDocumentsSection ? "collapse" : "show"}
                      >
                        <Icon
                          type="arrow-up"
                          className={classes.collapseIcon}
                          style={{
                            fontSize: "20px",
                            transform: `rotate(${
                              showDocumentsSection ? "0" : "180deg"
                            })`,
                          }}
                        />{" "}
                        &nbsp;
                      </Tooltip>
                      <span className={classes.toggleText}>Documents</span>
                    </h4>

                    {showDocumentsSection && (
                      <div>
                        <div>
                          {patientDocumentCreatePermission && (
                              <div>
                                <Button
                                  type="primary"
                                  onClick={this.addDocument}
                                >
                                  Add new document
                                </Button>
                                <br />
                                <br />
                              </div>
                            )}

                          <Modal
                            visible={!!documentEdit}
                            onCancel={this.onDocumentEditHide}
                            footer={null}
                          >
                            {documentEdit && (
                              <DocumentForm
                                {...documentEdit}
                                patient={subject}
                                withPatient
                                isNew={documentEdit && !documentEdit.id}
                                onSubmit={this.onDocumentEditSubmit}
                                onCancel={this.onDocumentEditHide}
                              />
                            )}
                          </Modal>
                        </div>
                        <div>
                          {get(subject, "profile.documents") && (
                            <Table
                              columns={this.documentColumns()}
                              onChange={this.handleTableChange}
                              dataSource={sortBy(get(subject, "profile.documents", []), (item) => order === 'asc' ? -item.order_index : item.order_index )}
                              loading={isLoading}
                              rowKey="id"
                              pagination={false}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {isLoading && <Icon type="loading" />}
              </div>
            )}
          </Content>
          <Footer noAdminLink />
        </Layout>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  patientDocumentCreatePermission: getPatientDocumentCreatePermission(state),
  patientProfileReadPermission: getPatientProfileReadPermission(state),
  consentGivenUpdatePermission: getConsentGivenUpdatePermission(state),
});

const mapDispatchToProps = { logout };

export default isAuthenticated(
  connect(mapStateToProps, mapDispatchToProps)(PatientProfile)
);



// WEBPACK FOOTER //
// ./src/containers/PatientProfile/PatientProfile.js